import * as Sentry from '@sentry/nextjs'
import { useState, type SetStateAction } from 'react'

import { type HandleReserveEventOptions } from '../../../hooks/useEventActions/interface'
import useEventActions from '../../../hooks/useEventActions/useEventActions'
import useServices from '../../../hooks/useServices'
import useUser from '../../../hooks/useUser'
import { handleError } from '../../../utilities/handlerError'
import Toaster from '../../../utilities/toaster'
import { PAYMENT_SCREEN } from '../../reservation/types'

type PaymentProps = {
  readonly handleOnPayment: (paymentMethod: string, onFinished?: () => void) => Promise<void>
  readonly handleSendVoucher: (voucherCode: string) => Promise<void>
  readonly handleReserveFree: (voucherCode: string) => Promise<void>
  readonly handleReserveWithSubscription: (userSubscriptionId: number) => Promise<void>
  readonly handleReserveWithBenefits: (companyEventQuotaId: number) => Promise<void>
  readonly isPending: boolean
  readonly voucherCode: string
  readonly setVoucherCode: React.Dispatch<SetStateAction<string>>
  readonly discountedPrice: number
}

const usePayment = ({ slot, therapist, setSelectedScreen, onCloseModal }): PaymentProps => {
  const { user } = useUser()
  const { isCounselling } = useServices()
  const { onReserveEvent } = useEventActions()
  const [voucherCode, setVoucherCode] = useState<string>('')
  const [isPending, setIsPending] = useState(false)
  const [discountedPrice, setDiscountedPrice] = useState<number | null>(null)

  const handleReserve = async (props: HandleReserveEventOptions, onFinished?: () => void) => {
    try {
      setIsPending(true)
      const res = await onReserveEvent({ ...props, isCounselling })

      if (res?.discountedPrice) {
        setSelectedScreen(PAYMENT_SCREEN.DEFAULT)
        setDiscountedPrice(parseInt(res.discountedPrice, 10))
        Toaster.success('component.toaster.reservationVoucherApplied')
      } else {
        if (onFinished) onFinished()
        onCloseModal()
      }
    } catch (e: any) {
      if (e?.data?.status === 404) {
        // valid case where backend refused to reserve the event
        // we may extend this to handle other cases once we see them in sentry
      } else if (e?.data?.type === 'NO_VOUCHER_CODE') {
        // valid case where used voucher is not valid
      } else {
        Sentry.captureException(e, { tags: { type: 'paymentError' } })
        console.log(e)
      }

      handleError('reserveAppointment', e)
    } finally {
      setIsPending(false)
    }
  }

  const handleOnPayment = async (paymentMethod: string, onFinished?: () => void) => {
    const membershipId = user.membership?.id
    return handleReserve(
      {
        paymentMethod,
        therapist,
        slot,
        ...(discountedPrice ? { voucherCode } : {}),
        ...(membershipId ? { membershipId } : {}),
      },
      onFinished,
    )
  }

  const handleSendVoucher = async (voucherCode: string) => {
    setVoucherCode(voucherCode)
    return handleReserve({ voucherCode, therapist, slot })
  }

  const handleReserveFree = async () => handleReserve({ therapist, slot })

  const handleReserveWithSubscription = async (userSubscriptionId: number) =>
    handleReserve({ userSubscriptionId, therapist, slot })

  const handleReserveWithBenefits = async (companyEventQuotaId: number) =>
    handleReserve({ companyEventQuotaId, therapist, slot })

  return {
    handleOnPayment,
    handleSendVoucher,
    handleReserveFree,
    handleReserveWithSubscription,
    handleReserveWithBenefits,
    isPending,
    voucherCode,
    setVoucherCode,
    discountedPrice,
  }
}

export default usePayment
